/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { startWith, switchMap } from 'rxjs/operators';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

@Component({
  standalone: false,
  selector: 'lib-cm-chips',
  templateUrl: './cm-chips.component.html',
  styleUrls: ['./cm-chips.component.css'],
  host: { '[class.disabled]': 'disabled' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CmChipsComponent),
      multi: true,
    },
  ],
})
export class CmChipsComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  myControl = new FormControl();

  separatorKeysCodes: number[] = [ENTER, COMMA];

  filteredOptions!: Observable<any>;

  vm: any;

  @Input() observableSourceFunction!: (param: {}) => Observable<any>;

  @Input() login: any;

  @Input() withAutocomplete = true;

  @Input() source: any;

  @Input() keyValue!: string;

  @Input() viewValue = 'name';

  @Input() params = {};

  @Input() disabled!: boolean;

  @Input() clearable!: boolean;

  @Input() highlightIfSet!: boolean;

  @Input() placeholder = 'Pick One';

  @Input() filterList!: Array<any>;

  @Input() cursorToBegin = false;

  @Input() chips: string[] = [];

  @Input() isViewMode = false;

  @Input() hasError = false;

  @Input() validateFn?: (value: string) => boolean;

  @Output() valueChange = new EventEmitter(true);

  @Output() validationError = new EventEmitter<string>();

  private isDomInitialized!: boolean;

  private onTouched!: () => void;

  private propagateChange!: (value: any) => void;

  aliases!: Array<string>;

  @ViewChild('trigger', { static: true }) trigger!: MatAutocompleteTrigger;

  @ViewChild('input', { static: true }) input!: ElementRef<HTMLInputElement>;

  constructor(public router: Router) {}

  ngOnInit(): void {
    this.vm = '';
    this.isDomInitialized = false;
    this.onTouched = () => {};
    this.propagateChange = () => {};

    if (!this.isViewMode && this.withAutocomplete) {
      this.filteredOptions = this.myControl.valueChanges.pipe(
        startWith(this.vm || ' '),
        switchMap((keyword) => {
          const params = {
            [this.viewValue]:
              (keyword && (keyword.name || keyword[this.viewValue])) || keyword || ' ',
            ...this.params,
          };
          return this.observableSourceFunction(params);
        }),
      );
    }
  }

  ngAfterViewInit(): void {
    this.isDomInitialized = true;
  }

  writeValue(value: any): void {
    this.vm = value || '';
    this.myControl.patchValue(this.vm);
  }

  registerOnChange(cb: any): void {
    this.propagateChange = cb;
  }

  registerOnTouched(cb: any): void {
    this.onTouched = cb;
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    if (!this.isDomInitialized) {
      return;
    }
    this.vm = event.option.value === null ? '' : event.option.value;
    this.propagateChange(event.option.viewValue);
    this.onTouched();
    this.addChip(event.option.viewValue);
    this.setCursorToBegin();
  }

  displayProperty(subj: any): string {
    return subj ? subj[this.viewValue] : '';
  }

  onBlur(): void {
    this.writeValue(this.vm);
  }

  reset(): void {
    this.writeValue('');
    this.propagateChange(this.vm);
  }

  focus(): void {
    setTimeout(() => {
      this.trigger.openPanel();
      this.input.nativeElement.focus();
    });
  }

  openAutoCompletePanel(): void {
    if (this.withAutocomplete) {
      setTimeout(() => {
        this.trigger.openPanel();
        this.myControl.updateValueAndValidity({ emitEvent: true });
      });
    }
  }

  add(event: MatChipInputEvent): void {
    if (this.disabled) {
      return;
    }
    const value = (event.value || '').trim();
    if (!value) {
      return;
    }
    if (this.validateFn && !this.validateFn(value)) {
      this.validationError.emit(value);
      event.chipInput?.clear();
      this.myControl.setValue('', { emitEvent: false });
      return;
    }
    this.addChip(value);
  }

  remove(index: number): void {
    if (this.disabled || index < 0) {
      return;
    }
    this.chips.splice(index, 1);
    this.myControl.updateValueAndValidity();
  }

  private addChip(value: string): void {
    if (value) {
      this.chips.push(value);
    }
    this.input.nativeElement.value = '';
    this.valueChange.emit(this.chips);
    this.myControl.setValue('');
    this.openAutoCompletePanel();
  }

  private setCursorToBegin(): void {
    if (!this.cursorToBegin) {
      return;
    }
    this.input.nativeElement.blur();
    this.input.nativeElement.setSelectionRange(0, 0);
    this.input.nativeElement.focus();
  }
}
